/**
 * R58-L-17 — read + write the per-user Analysis-Viewer settings persisted
 * under `auth/v1@4.7.0`'s `LysePreferences.viewer` block.
 *
 *   maxZoomPercent  — caps drag-rectangle + mouse-wheel zoom (R58-L-16).
 *   showDebugOverlay — toggles the developer pixel-coord readout (R58-L-15).
 *
 * Reads come from `useLysePreferencesQuery()` (TanStack Query — shared
 * cache with the rest of the Settings drawer); writes go through
 * `useUpdateLysePreferencesMutation()` (500 ms debounce, optimistic
 * cache write). The full `viewer` block is sent on every change so the
 * BE's "top-level last-write-wins" PATCH semantics never end up with a
 * half-populated `{ maxZoomPercent }` row.
 */
import { useCallback } from "react";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  type ViewerPreferences,
} from "../../preferences/index.js";

export interface UseViewerSettingsApi {
  /** Effective viewer prefs. Never undefined — defaults applied client-side. */
  viewer: ViewerPreferences;
  /** Set `maxZoomPercent` (clamps to [100, 1000] before PATCHing). */
  setMaxZoomPercent: (next: number) => void;
  /** Toggle / set `showDebugOverlay`. */
  setShowDebugOverlay: (next: boolean) => void;
  /** True while the debounced PATCH is in flight. */
  isSaving: boolean;
}

const MIN_ZOOM = 100;
const MAX_ZOOM = 1000;

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

export function useViewerSettings(): UseViewerSettingsApi {
  const { prefs } = useLysePreferencesQuery();
  const { update, isSaving } = useUpdateLysePreferencesMutation();

  // `prefs.viewer` is defaulted by the zod schema — guaranteed present
  // at this layer.
  const viewer: ViewerPreferences = prefs.viewer;

  const setMaxZoomPercent = useCallback(
    (next: number) => {
      const clamped = clamp(Math.round(next), MIN_ZOOM, MAX_ZOOM);
      update({
        viewer: {
          maxZoomPercent: clamped,
          showDebugOverlay: viewer.showDebugOverlay,
        },
      });
    },
    [update, viewer.showDebugOverlay],
  );

  const setShowDebugOverlay = useCallback(
    (next: boolean) => {
      update({
        viewer: {
          maxZoomPercent: viewer.maxZoomPercent,
          showDebugOverlay: next,
        },
      });
    },
    [update, viewer.maxZoomPercent],
  );

  return { viewer, setMaxZoomPercent, setShowDebugOverlay, isSaving };
}
